import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Calendar, Clock, Tag, ArrowLeft, Share2, Facebook, Twitter, Linkedin } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function BlogDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/api/blog/${slug}`);
      setPost(response.data);
      
      // Fetch related posts
      const relatedResponse = await axios.get(`${API}/api/blog?limit=3`);
      setRelatedPosts(relatedResponse.data.posts.filter(p => p.slug !== slug).slice(0, 3));
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching blog post:", error);
      toast.error("Blog post not found");
      navigate("/blog");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const sharePost = (platform) => {
    const url = window.location.href;
    const title = post?.title || '';
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied to clipboard!");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white py-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading article...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      {/* Back Button */}
      <div className="max-w-4xl mx-auto px-4 pt-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/blog')}
          className="text-gray-600 hover:text-orange-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blog
        </Button>
      </div>

      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 py-8">
        {/* Category Badge */}
        <div className="mb-4">
          <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-semibold">
            {post.category}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Playfair Display' }}>
          {post.title}
        </h1>

        {/* Meta Information */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-8 pb-8 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(post.publish_date)}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4" />
            <span>{post.read_time} min read</span>
          </div>
          
          {/* Share Buttons */}
          <div className="ml-auto flex items-center space-x-2">
            <span className="text-gray-500 mr-2">Share:</span>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => sharePost('facebook')}
              className="hover:text-blue-600"
            >
              <Facebook className="w-4 h-4" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => sharePost('twitter')}
              className="hover:text-blue-400"
            >
              <Twitter className="w-4 h-4" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => sharePost('linkedin')}
              className="hover:text-blue-700"
            >
              <Linkedin className="w-4 h-4" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={copyLink}
              className="hover:text-orange-600"
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Featured Image */}
        {post.image_url && (
          <div className="mb-10 rounded-lg overflow-hidden shadow-lg">
            <img 
              src={post.image_url} 
              alt={post.title}
              className="w-full h-auto"
              onError={(e) => {
                e.target.src = 'https://placehold.co/1200x600/FF6B35/FFFFFF/png?text=Its-Sold.com+Blog';
              }}
            />
          </div>
        )}

        {/* Article Content */}
        <div 
          className="prose prose-lg max-w-none mb-12"
          style={{
            lineHeight: '1.8',
            fontSize: '18px'
          }}
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-8 pt-8 border-t border-gray-200">
          <Tag className="w-5 h-5 text-gray-500 mr-2" />
          {post.tags.map((tag, idx) => (
            <span 
              key={idx}
              className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-orange-100 hover:text-orange-700 transition cursor-pointer"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* CTA Box */}
        <Card className="bg-gradient-to-r from-orange-50 to-red-50 p-8 border-2 border-orange-200 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">Ready to Save Thousands on Your Home Sale?</h3>
          <p className="text-gray-700 mb-6">
            List your DFW property for FREE on Its-Sold.com and keep 100% of your equity. No commissions, no hidden fees.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/register')}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            List Your Home FREE Today
          </Button>
        </Card>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Card 
                  key={relatedPost.slug}
                  className="overflow-hidden hover:shadow-lg transition-all cursor-pointer"
                  onClick={() => {
                    navigate(`/blog/${relatedPost.slug}`);
                    window.scrollTo(0, 0);
                  }}
                >
                  <div className="h-40 bg-gradient-to-br from-orange-400 to-red-500">
                    <img 
                      src={relatedPost.image_url} 
                      alt={relatedPost.title}
                      className="w-full h-full object-cover opacity-90"
                      onError={(e) => {
                        e.target.src = 'https://placehold.co/400x300/FF6B35/FFFFFF/png?text=Its-Sold.com';
                      }}
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 hover:text-orange-600 transition">
                      {relatedPost.title}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}
      </article>

      {/* Bottom CTA */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 py-12 mt-20">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Join Thousands of Smart DFW Homeowners</h2>
          <p className="text-xl text-orange-100 mb-8">
            Save an average of $15,000 by selling FSBO with Its-Sold.com
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/register')}
            className="bg-white text-orange-600 hover:bg-gray-100 text-lg px-8 py-6"
          >
            Get Started - It's FREE
          </Button>
        </div>
      </div>
    </div>
  );
}
